import { store } from "../core/store";
import { socket } from "../core/socket";
import { loadIdentity, saveIdentity } from "../core/storage";
import { LOBBY_COLOR_ORDER } from "./lobby";

/** Opens the "change your color" overlay, seeded with the player's current color
 * selected. Self-contained like toast.ts - no init step, just looks up its own DOM
 * nodes on demand, since it's only ever triggered from one place (clicking your own
 * swatch in the player list, itself gated to the waiting room client- and
 * server-side). */
export function openColorPicker(currentColor: string): void {
  const overlay = document.getElementById("color-picker-overlay");
  const modal = document.getElementById("color-picker-modal");
  if (!overlay || !modal) return;

  function onOverlayClick(e: MouseEvent): void {
    if (e.target === overlay) close();
  }

  function close(): void {
    overlay!.classList.add("hidden");
    overlay!.removeEventListener("click", onOverlayClick);
  }

  overlay.addEventListener("click", onOverlayClick);

  function render(errorMessage?: string): void {
    modal!.innerHTML = "";

    const header = document.createElement("div");
    header.className = "settings-header";
    const title = document.createElement("h2");
    title.textContent = "Change Your Color";
    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "close-button";
    closeBtn.textContent = "✕";
    closeBtn.addEventListener("click", close);
    header.append(title, closeBtn);
    modal!.appendChild(header);

    if (errorMessage) {
      const err = document.createElement("p");
      err.className = "form-error";
      err.textContent = errorMessage;
      modal!.appendChild(err);
    }

    const grid = document.createElement("div");
    grid.className = "color-grid";
    const taken = new Set((store.getState().snapshot?.players ?? []).map((p) => p.color));
    for (const color of LOBBY_COLOR_ORDER) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "color-grid-swatch";
      btn.style.backgroundColor = color;
      // Your own current color is never "taken" from your own perspective.
      const isTakenByOther = color !== currentColor && taken.has(color);
      btn.disabled = isTakenByOther;
      if (isTakenByOther) btn.classList.add("taken");
      if (color === currentColor) btn.classList.add("selected");
      btn.addEventListener("click", () => {
        if (color === currentColor) {
          close();
          return;
        }
        // Block further clicks during the round trip WITHOUT setting the `disabled`
        // attribute - `:disabled` is what draws the red "taken" strikethrough (see
        // main.css), so disabling every swatch here would flash all twelve as
        // blocked for the split second before the ack arrives and the modal closes.
        grid.style.pointerEvents = "none";
        socket.emit("player:changeColor", color, (res) => {
          if (res.ok) {
            const lobbyCode = store.getState().lobbyCode;
            const identity = lobbyCode ? loadIdentity(lobbyCode) : null;
            if (lobbyCode && identity) saveIdentity(lobbyCode, { ...identity, color });
            close();
          } else {
            render(res.reason ?? "Unable to change color.");
          }
        });
      });
      grid.appendChild(btn);
    }
    modal!.appendChild(grid);
  }

  render();
  overlay.classList.remove("hidden");
}
